import { z } from 'zod';
import { recommendationSchema } from './finding-dto';

/**
 * Bumped whenever the stored `Persona` shape changes in a way older readers can't parse. Stamped
 * on every write and checked on read: personas carrying any other version are skipped, not
 * migrated — the same contract `Run` uses (see `CURRENT_RUN_SCHEMA_VERSION`).
 *
 * 2 added the required `isCore` flag, which replaced a hard-coded `vp_product` id.
 */
export const CURRENT_PERSONA_SCHEMA_VERSION = 2 as const;

/**
 * Every text field is interpolated verbatim into the agent's system prompt, so each is length
 * capped — an unbounded role definition is an unbounded prompt.
 */
const PROMPT_FIELD_MAX = 2000;

export const personaSchema = z.object({
  version: z.literal(CURRENT_PERSONA_SCHEMA_VERSION),
  id: z
    .string()
    .min(1)
    .max(64)
    .regex(/^[a-z0-9_]+$/, 'must be lowercase letters, digits and underscores'),
  label: z.string().min(1).max(60),
  headline: z.string().max(PROMPT_FIELD_MAX),
  lens: z.string().max(PROMPT_FIELD_MAX),
  recommendationStyle: z.string().max(PROMPT_FIELD_MAX),
  discoveryHints: z.string().max(PROMPT_FIELD_MAX),
  relevanceNotes: z.string().max(PROMPT_FIELD_MAX),
  avoid: z.string().max(PROMPT_FIELD_MAX),
  /**
   * The role a run is authored as before being re-voiced for the others; it owns each finding's
   * root-level statement and recommendations and never appears in `personaOverrides`. Exactly one
   * role in a list carries it — see `personaListSchema`.
   *
   * Which role a *stored* run was authored as is `Run.personaId`, not this flag: changing the core
   * role must not retroactively re-attribute runs already written.
   */
  isCore: z.boolean(),
});

export const MAX_PERSONAS = 20;

export const personaListSchema = z
  .array(personaSchema)
  .min(1)
  .max(MAX_PERSONAS)
  .superRefine((personas, ctx) => {
    const seen = new Set<string>();
    personas.forEach((persona, index) => {
      if (seen.has(persona.id)) {
        ctx.addIssue({
          code: 'custom',
          path: [index, 'id'],
          message: `Duplicate role id "${persona.id}"`,
        });
      }
      seen.add(persona.id);
    });

    // Exactly one, so the agent never has to guess which role to author as — the array order used
    // to decide it by accident whenever the expected id was missing.
    const core = personas.filter((persona) => persona.isCore);
    if (core.length !== 1) {
      ctx.addIssue({
        code: 'custom',
        path: [],
        message: `Exactly one role must be the core role, found ${core.length}`,
      });
    }
  });

export const personasResponseSchema = z.object({
  personas: z.array(personaSchema),
});

export const personasWriteSchema = z.object({
  personas: personaListSchema,
});

export const personaViewSchema = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('override'),
    findingId: z.string().min(1),
    businessStatement: z.string().min(1),
    recommendations: z.array(recommendationSchema).min(1),
  }),
  z.object({
    kind: z.literal('withheld'),
    findingId: z.string().min(1),
    reason: z.string().min(1),
  }),
]);

export const personaOverrideResultSchema = z.object({
  views: z.array(personaViewSchema),
});

export type Persona = z.infer<typeof personaSchema>;
export type PersonasResponse = z.infer<typeof personasResponseSchema>;
export type PersonasWrite = z.infer<typeof personasWriteSchema>;
export type PersonaOverrideResult = z.infer<typeof personaOverrideResultSchema>;
